// AiModelSwitcher — THE single quick model-mode switcher, shared by the
// assistant chat header (_simone/Chat.js) and the canvas prompt bar
// (workspace/app.js ModelBtn) so the two can never drift (2026-08-12 per
// Nika "canvasPromptTextarea and ai assistant should be using same model
// switcher and settings buttons").
//
// Four exports, one implementation:
//   • AiModelSwitcherTrigger — the "✦ ⌄" button (the model in its aria-label
//     and in the open menu — no name on the button, 2026-09-29). Its DEFAULT onClick
//     drives the SELF-ANCHORED composite below (root._modelMenuOpen) so the
//     switcher works on surfaces without the @symbo.ls/dropdown plugin
//     (standalone simone :1235, chrome-connect, ext-ui). Hosts that have
//     the plugin (the workspace shell composer) override onClick with
//     openDropdown + AiModelSwitcherBody instead.
//   • AiModelSwitcherBody — the menu body (heading + option rows). No
//     positioning / outside-click guard of its own — the host (the
//     composite's Picker or the dropdown plugin panel) owns those. The
//     same split AiSettingsPanel already uses for the settings body.
//   • AiModelSwitcherRow — one registered option row (label + sub + check),
//     fed per-row state by the body's Options collection.
//   • AiModelSwitcher — the composite trigger + anchored picker with the
//     house Dropdown motion recipe. What Chat.js mounts in its header.
//
// Selection writes root.modelMode + the localStorage key sdk.ai.getModelMode
// reads (`symbols_ai_model_mode`) so the NEXT send carries body.modelMode —
// the same contract AiSettingsPanel's provider section implements (that
// panel stays the deep, scope-aware surface; this is the one-tap switch).

import { AI_MODEL_ROWS, aiModelCatalogRows } from '../functions/aiModelCatalog.js'
// THE house keyboard model for menus — see functions/menuKeyboard.js. Reached
// as scope VALUES below (never as bare identifiers inside a factory: a
// frank.toJSON lift drops those — FA204).
import {
  menuArrowNav,
  menuFocusOnOpen,
  menuNodeVisible,
  restoreMenuFocus
} from '../functions/menuKeyboard.js'

export const AiModelSwitcherTrigger = {
  // FA204 / frank trap — the label map lives in scope: a bare module-scope
  // const read from a serialized prop factory reads as undefined once this
  // component is frank.toJSON'd for platform delivery.
  scope: {
    // ArrowDown/ArrowUp on the CLOSED trigger opens the menu — the standard
    // menu-button contract. Enter/Space need no handler: this is a real
    // <button>, so both already fire the onClick that opens it.
    openByKey(e, el, s) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return false
      e.preventDefault()
      e.stopPropagation()
      if (s.root._modelMenuOpen) return true
      s.root.update({ _modelMenuOpen: true })
      try {
        el.call('fetchAiProviderDiagnostics')
      } catch (_) {}
      return true
    },
    // The model's name, the way its own menu rows name it: the ONE catalog
    // (functions/aiModelCatalog.js, held as a scope VALUE — FA204), its label
    // through polyglot — a bundle key translates (Auto / Research / Cheap:
    // "ავტომატური" in a ka UI), a product name stays Latin (polyglot echoes
    // it; I18N glossary rule 4). An unknown mode reads as Auto.
    MODEL_ROWS: AI_MODEL_ROWS,
    modelName(el, mode) {
      const rows = this.MODEL_ROWS
      const row = rows.find((r) => r.key === mode) || rows.find((r) => r.key === 'auto')
      return el.call('polyglot', row.label)
    }
  },
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'X',
  align: 'center center',
  padding: 'Y2 Y',
  // THE ROW'S SQUARE on every width: the icon-button law's control size
  // (`controlHCompact`, the token the composer row's own button floor uses) —
  // measured on dev 37211a590 at 1440 the button was 35.3 × 33.3 px beside
  // the row's 36 px icon buttons. Border-box: the padding sits INSIDE the
  // floor, never on top of it (a content-box floor grew the phone send to
  // 56 px, 40e9d2445).
  minWidth: 'controlHCompact',
  minHeight: 'controlHCompact',
  boxSizing: 'border-box',
  round: 'radiusControl',
  theme: 'tertiary',
  color: 'title',
  fontWeight: '400',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  whiteSpace: 'nowrap',
  // House trigger states: hover wash at rest, `selected` while its menu is
  // open (the BoardMenu MenuTrigger recipe). isOpen only flips on the
  // self-anchored composite (root._modelMenuOpen) — the shell composer host
  // overrides onClick with openDropdown and never writes the flag, so the
  // block is simply inert there.
  ':hover': { background: 'hover' },
  isOpen: (el, s) => !!s.root._modelMenuOpen,
  '.isOpen': { background: 'selected' },
  // THE COMPACT FORM — "✦" + caret in every language and on every width
  // (current-2a's decision, 2026-09-29, Nika's "no cut text"): measured on
  // dev a9783341a at 1440, the English composer row beside the Dashboard chip
  // overflowed for every model but Auto (Cheap +8 … OpenRouter +45 px) and its
  // shrink cut the chip's word to "…"; "✦ ავტომატური" never fitted. About a
  // 36 px square; the model name lives in the accessible name below and in the
  // open menu. A host that re-states a label-era inline padding (the
  // composer's ModelBtn: 'Y Z2') still gets the square on phones:
  '@mobileL': { paddingInline: 'Y' },
  // The name carries the model ("Switch AI model: Auto") — the button shows
  // no name on any width, so the accessible name is where the model is read.
  'aria-label': (el, s) =>
    el.call('polyglot', 'ai.settings.switchModel') +
    ': ' +
    el.scope.modelName(el, (s && s.root && s.root.modelMode) || 'auto'),
  'aria-haspopup': 'menu',
  // Tracks the SELF-ANCHORED composite's own flag — the one host whose open
  // state this component owns. The shell composer's ModelBtn hands the panel
  // to the @symbo.ls/dropdown plugin, which owns (and announces) its own
  // panel lifecycle; that mount pins this to 'false' rather than reporting a
  // flag it never writes.
  ariaExpanded: (el, s) => (s.root._modelMenuOpen ? 'true' : 'false'),
  onKeydown: (e, el, s) => el.scope.openByKey(e, el, s),
  // The model button's one visible mark (the name is in the aria-label).
  Glyph: {
    tag: 'span',
    text: '✦'
  },
  // Caret glyph — key-based auto-extend (`Icon` extends the Icon built-in).
  // Flips while the composite's menu is open; static transition on
  // transform only (never on a reactive background — MenuPanel.js note).
  Icon: {
    name: 'arrow angle down',
    boxSize: 'Z',
    opacity: '.6',
    transition: 'A defaultBezier',
    transitionProperty: 'transform',
    isOpen: (el, s) => !!s.root._modelMenuOpen,
    '.isOpen': { transform: 'rotate(180deg)' }
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const opening = !s.root._modelMenuOpen
    s.root.update({ _modelMenuOpen: opening })
    // Refresh live per-provider diagnostics on OPEN only — cheap +
    // best-effort (see fetchAiProviderDiagnostics), never on toggle-close.
    if (opening) {
      try {
        el.call('fetchAiProviderDiagnostics')
      } catch (_) {}
    }
  }
}

export const AiModelSwitcherBody = {
  // FA204 / frank trap — rows + diagnostics gating live in scope (see the
  // Trigger note). The catalog + demotion are the SHARED
  // functions/aiModelCatalog.js exports, assigned here as scope VALUES and
  // reached via `this.…` (el.scope.modelRows() binds `this` to el.scope) —
  // never as bare imported identifiers from inside a scope method, which a
  // frank `to-json` lift would drop (`unresolvable-identifier`). The
  // workspace-pin picker (AiSettingsPanel PinSection) consumes the SAME two
  // values, so the two menus can never drift.
  scope: {
    MODEL_ROWS: AI_MODEL_ROWS,
    catalogRows: aiModelCatalogRows,
    // THE house keyboard model (functions/menuKeyboard.js), held as scope
    // VALUES so every reach from a serialized factory is `this.…`/
    // `el.scope.…` — the same FA204-safe shape catalogRows above uses.
    navRows: menuArrowNav,
    focusOnOpen: menuFocusOnOpen,
    nodeVisible: menuNodeVisible,
    focusBack: restoreMenuFocus,

    // Is this body's menu open RIGHT NOW? It renders in two hosts with two
    // different lifecycles and one predicate has to cover both:
    //   • the self-anchored composite (Chat header) keeps the panel mounted
    //     and hides it with `visibility: hidden` — `root._modelMenuOpen` is
    //     the truth, and offsetWidth is useless (a hidden panel keeps its
    //     layout box);
    //   • the shell composer hands the body to the @symbo.ls/dropdown plugin,
    //     which MOUNTS it only while open and never touches that flag — so
    //     "am I actually painted?" is the only available signal there.
    menuIsOpen(el, s) {
      if (s && s.root && s.root._modelMenuOpen) return true
      return this.nodeVisible(el && el.node)
    },

    // Arm the focus lifecycle. Edge-detected inside menuFocusOnOpen, so this
    // may be called from onRender AND every onUpdate without re-yanking focus
    // back to the first row while the user is three rows down.
    armFocus(el, s) {
      this.focusOnOpen(el, this.menuIsOpen(el, s), {
        when: (node) => this.nodeVisible(node)
      })
    },

    // Escape closes whichever host owns this body and hands focus back to the
    // trigger. Both close paths run unconditionally: the composite reads the
    // flag, the plugin host answers `closeDropdown`, and neither call harms
    // the other host (the flag is already false there; `closeDropdown` is a
    // best-effort el.call — portable consumers may not register it at all).
    closeMenu(el, s) {
      if (s && s.root && s.root._modelMenuOpen) s.root.update({ _modelMenuOpen: false })
      try {
        el.call('closeDropdown', 'aiModel')
      } catch (_) {}
      this.focusBack(el)
    },

    modelRows(el, s) {
      const current = s.root.modelMode || 'auto'
      // Live per-provider diagnostics (GET /core/ai/providers, fetched when
      // the menu opens — see the Trigger/ModelBtn onClick). Absent until the
      // first successful fetch, so every row defaults to enabled — the menu
      // degrades to plain labels, never to a broken/empty one. Which row is
      // ACTIVE is this menu's own question — the per-user root.modelMode.
      //
      // ONLY WHAT THIS SERVER CAN RUN (WS-AI-MODEL-SWITCHER-SHOWS-SIX-
      // DISABLED-PROVIDERS-1, QA on dev 2026-09-26: six of eight rows read
      // "Disabled on this server"). A provider the server cannot run is not a
      // choice, so its row is left out and one muted line (OffNote below)
      // says how many are off. 'auto' is never demoted and leads the list.
      // The member's CURRENT choice stays even when it went off, with the
      // live reason as its line — the menu never hides why a pick is not
      // answering.
      const rows = this.catalogRows(this.MODEL_ROWS, s.root.aiProviderDiagnostics)
      return rows
        .filter((m) => !m.disabled || m.key === current)
        .map((m) => ({ ...m, active: m.key === current }))
    },

    // How many rows the list above leaves out (off on this server, and not
    // the member's current choice) — the OffNote's count; 0 hides it.
    offCount(el, s) {
      const current = s.root.modelMode || 'auto'
      const rows = this.catalogRows(this.MODEL_ROWS, s.root.aiProviderDiagnostics)
      return rows.filter((m) => m.disabled && m.key !== current).length
    }
  },
  flow: 'y',
  gap: 'Y',
  textAlign: 'left',

  // ── keyboard (2026-09-02) ───────────────────────────────────────────────
  // One `role="menu"` over BOTH groups: `menuArrowNav` collects
  // `[role="menuitem"]` in DOM order from this node down, so ArrowDown walks
  // Model rows → Provider row and wraps, with no per-group wiring. The rows
  // are native <button>s (AiOptionRow), so Enter/Space already activate.
  // `tabindex: -1` makes the panel itself a programmatic focus target only —
  // never a tab stop — so `focusFirstMenuRow` can park focus here when the
  // catalog is momentarily empty and Escape still reaches this handler
  // through DOMQL's ancestor-walking keydown delegate.
  role: 'menu',
  tabindex: '-1',
  ariaLabel: (el) => el.call('polyglot', 'ai.modelSwitcher.ariaLabel'),
  subscribeTo: ['_modelMenuOpen'],
  onRender: (el, s) => el.scope.armFocus(el, s),
  onUpdate: (el, s) => el.scope.armFocus(el, s),
  // Plugin-hosted panels are UNMOUNTED on close (outside click, row pick, the
  // plugin's own Escape) — there is no true→false edge left to observe, so
  // the focus return happens here instead.
  onRemove: (el) => el.scope.focusBack(el),
  onKeydown: (e, el, s) => {
    if (el.scope.navRows(e, el.node)) return
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      el.scope.closeMenu(el, s)
      return
    }
    // Tab leaves the menu — close it on the way out (native menu behaviour),
    // WITHOUT preventDefault so the browser still moves focus onward. Drop
    // the stored return target FIRST: the close edge would otherwise pull
    // focus back to the trigger and fight the Tab the user just pressed.
    if (e.key === 'Tab') {
      el._menuReturnFocusTo = null
      if (s.root && s.root._modelMenuOpen) s.root.update({ _modelMenuOpen: false })
    }
  },

  // Unique key — `SectionHeader` is a built-in component; a plain key
  // renders just our label. Extends THE shared menu caption (AiMenu.js) so
  // this heading and the AiSettingsPanel section headers are one treatment.
  PvHeading: {
    extends: 'AiMenuHeading',
    text: '{{ ai.modelSwitcher.model | polyglot }}'
  },
  Options: {
    flow: 'y',
    gap: 'V',
    children: (el, s) => el.scope.modelRows(el, s),
    childrenAs: 'state',
    childExtends: 'AiModelSwitcherRow'
  },

  // One muted line when the server has some providers off — the count of
  // the rows the list leaves out, never a row per provider. Aligned to the
  // option rows' text edge like the section captions; not a menu item, so
  // the arrow keys walk past it.
  OffNote: {
    tag: 'p',
    margin: '0',
    paddingLeft: 'Z2',
    fontSize: 'Y',
    lineHeight: '1.4',
    color: 'inactive',
    hide: (el, s) => !el.scope.offCount(el, s),
    text: (el, s) =>
      el.call('polyglot', 'ai.modelSwitcher.someOff').replace('{count}', el.scope.offCount(el, s))
  },

  // ── Provider ────────────────────────────────────────────────────────────
  // Moved here from AiSettingsPanel's ProviderSection (2026-09-02 per Nika
  // "move AI provider section to model switcher both in AI prompt and
  // sidebar") — this body renders in BOTH surfaces, so one declaration
  // covers them. The one provider the model rows cannot express is the
  // machine-local simone-bridge target; its row logic is inlined (not a
  // cross-file scope reach — frank `to-json` lifts drop same-file sibling
  // helpers, let alone another component's scope). Selecting it toggles
  // targetMode exactly like the settings panel's chooseLocal did, opening
  // the AiBridgeModal shell overlay on the way in. Because this menu now
  // owns the ONLY exit from local mode, the two mount-site gates that hid
  // the switcher in local mode are dropped in the same change (Chat.js
  // header, app.js ModelBtn) — hiding it again would trap local mode.
  ProviderHeading: {
    extends: 'AiMenuHeading',
    text: '{{ ai.modelSwitcher.provider | polyglot }}'
  },
  LocalRow: {
    extends: 'AiOptionRow',
    cursor: 'pointer',
    opacity: '1',
    title: '',
    background: (el, s) =>
      (s.root.targetMode || 'general') === 'local' ? 'selected' : 'transparent',
    ':hover': { background: 'hover' },
    // The row base reads per-row state for these slots; this named child's
    // `s` is the menu state, so pin them off deterministically.
    // (Icon = leading glyph slot, Icon_1 = trailing check — AiOptionRow.)
    Icon: { if: () => false },
    Meta: { if: () => false },
    Body: {
      Label: { text: '{{ ai.settings.localAgent | polyglot }}' },
      // Sub SWAPS its text while local instead of gaining a second line, so
      // the row never grows and shifts its neighbours (the house rule every
      // AiOptionRow follows).
      Sub: {
        tag: 'div',
        text: (el, s) =>
          el.call(
            'polyglot',
            (s.root.targetMode || 'general') === 'local'
              ? 'ai.settings.localAgentSubActive'
              : 'ai.settings.localAgentSub'
          )
      }
    },
    Icon_1: {
      hide: (el, s) => (s.root.targetMode || 'general') !== 'local'
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = el.state && el.state.root
      if (!root || !root.update) return
      const leaving = (s.root.targetMode || 'general') === 'local'
      const next = leaving ? 'workspace' : 'local'
      root.update({ targetMode: next, _aiBridgeSetupOpen: !leaving, _modelMenuOpen: false })
      try {
        el.call('onTargetModeChange', next)
      } catch (_) {}
      try {
        el.call('closeDropdown', 'aiModel')
      } catch (_) {}
    }
  }
}

// One model-mode row — registered (not an inline childExtends shape) so the
// menu body stays frank-lean and the row is reusable/overridable by key.
// Anatomy + states come from THE shared AiOptionRow (AiMenu.js — MenuPanelItem
// metrics, selected wash + title check, disabled handling); this export only
// adds the model-mode selection behaviour. Per-row state (fed via
// childrenAs:'state' from Options above): { key, label, sub, active, disabled }
export const AiModelSwitcherRow = {
  extends: 'AiOptionRow',
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    // A disabled row is a real click target (so its reason is reachable
    // via title/hover), just not a selectable one.
    if (s.disabled) return
    const root = el.state && el.state.root
    if (root && root.update) root.update({ modelMode: s.key, _modelMenuOpen: false })
    // Persist to the key sdk.ai.getModelMode reads so the next send
    // carries body.modelMode. The el.call hops are best-effort — the
    // shell registers setModelMode + closeDropdown; portable consumers
    // (standalone simone / extension panels) may have neither.
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('symbols_ai_model_mode', s.key)
    } catch (_) {}
    try {
      el.call('setModelMode', s.key)
    } catch (_) {}
    try {
      el.call('closeDropdown', 'aiModel')
    } catch (_) {}
  }
}

// The self-anchored composite — trigger + picker in one mount, no dropdown
// plugin required. The picker is THE shared AiMenuPanel (AiMenu.js): the
// glass surface, radiusCard, 60vh scroll cap and the house open/close motion
// (hidden base + `.isOpen` conditional block) all come from the base — only
// the anchor positioning and the isOpen predicate live here.
export const AiModelSwitcher = {
  position: 'relative',
  flow: 'x',
  gap: 'X',
  align: 'center',

  // Key-based auto-extend — the key IS the registered trigger component.
  AiModelSwitcherTrigger: {},

  Picker: {
    extends: 'AiMenuPanel',
    isOpen: (el, s) => !!s.root._modelMenuOpen,
    tag: 'div',
    position: 'absolute',
    top: '110%',
    right: '0',
    zIndex: '200',

    onClick: (e) => e.stopPropagation(),
    // Outside-press closes. FA503: declared, so DOMQL registers it once and
    // removes it in dispose() — no `el._docClickGuard` mirror field.
    onDocumentMousedown: {
      capture: true,
      handler: (ev, el) => {
        const node = el && el.node
        if (!node || node.contains(ev.target)) return
        const root = el.state && el.state.root
        if (root && root._modelMenuOpen) root.update({ _modelMenuOpen: false })
      }
    },

    AiModelSwitcherBody: {}
  }
}
